import { LayerSystem } from './LayerSystem';
import { AnimQueue } from './AnimQueue';
import { TransitionSystem } from './TransitionSystem';
import { fluidSize } from '../primitives/layout';
import { createHeapTopologyState, appendNodeToHeap } from '../topology/heap4';
import { buildSlideOrderFromHeap } from '../topology/traversal';
import { computeFourSlotRegions, computeSequentialLeftAlignedRegions } from '../topology/regions';
import { planAnimationTimeline, planInstructionalTimeline } from './TimelinePlanner';
import { collectPedagogicalContractDiagnostics } from './pedagogicalContractDiagnostics';

const State = {
  IDLE: 'IDLE',
  PREPARING: 'PREPARING',
  DWELL_BEFORE: 'DWELL_BEFORE',
  COMMITTING: 'COMMITTING',
  ANIMATING: 'ANIMATING',
  DWELL_AFTER: 'DWELL_AFTER',
  TRANSITIONING: 'TRANSITIONING',
};

function normalizeAnimationPlan(plan) {
  if (!Array.isArray(plan)) return [];
  return plan
    .filter((step) => step && typeof step === 'object')
    .map((step, idx) => ({
      step: Number.isFinite(step.step) ? step.step : idx + 1,
      target: String(step.target || 'content'),
      effect: String(step.effect || 'fade-in'),
      durationSec: Number.isFinite(step.durationSec) ? Math.max(0, step.durationSec) : 0.25,
      waitForSignal: Boolean(step.waitForSignal),
      deps: Array.isArray(step.deps)
        ? step.deps.filter((dep) => Number.isFinite(dep)).map((dep) => Number(dep))
        : [],
      overlapSec: Number.isFinite(step.overlapSec) ? Math.max(0, step.overlapSec) : 0,
      startAfterSec: Number.isFinite(step.startAfterSec) ? Math.max(0, step.startAfterSec) : 0,
      checkpoint: Boolean(step.checkpoint),
    }))
    .sort((a, b) => a.step - b.step);
}

export class LessonRunner {
  constructor(canvas, lessonSpec, registry) {
    this.canvas = canvas;
    this.lessonSpec = lessonSpec;
    this.registry = registry;

    this.layers = new LayerSystem(canvas);
    this.anim = new AnimQueue();
    this.transitions = new TransitionSystem(this.layers, this.anim);

    this.nodes = [];
    this.packets = new Map();
    this.nodeById = new Map();
    this.topology = null;
    this.slideOrder = [];
    this.nodeRuntimeMeta = {};
    this.generation = 0;
    this.destroyed = false;
    this.speedFactor = 1.0;
    this.rafId = null;
    this.lastTime = 0;
    this.resizeObserver = null;
    this.resizeDebounceTimer = null;
    this.paused = false;
    this.manipulateMode = false;
    this.showRegionGuides = false;
    this.zoom = null;
    this.isTransitioning = false;
    this.currentSlideId = null;
    this.slidePhase = 'mounting';
    this.animationStepIndex = -1;
    this.currentAnimationTimeline = [];
    this.instructionalEvents = [];
    this.eventById = new Map();
    this.eventByNodeId = new Map();
    this.eventLifecycle = new Map();
    this.checkpoints = new Map();
    this.pendingPlaybackAfterManualNode = false;
    this.pendingStartNodeId = null;
    this.pendingTimelineSeek = null;
    this.slideSignals = new Map();
    this.interaction = {
      hoverSlot: null,
      hoverChildId: null,
      selectedSlot: null,
      selectedChildId: null,
    };
    this.boundPointerMove = null;
    this.boundClick = null;
    this.contractDiagnostics = collectPedagogicalContractDiagnostics(lessonSpec?.nodes);

    this.state = State.IDLE;
    this.currentNodeIndex = 0;

    this.layers.setTheme(lessonSpec.theme.board);
    this.layers.setLayout(lessonSpec.theme.layout);
    this.speedFactor = lessonSpec.runtime.speedFactor;
    this.initializeInstructionalEvents();

    this.setupResize();
    this.setupInteraction();
    this.initialResize();
  }

  setupResize() {
    // Canvas DPR resize is now handled by useResizeCanvas hook in parent
    // We only need to watch for size changes to restart the lesson
    this.resizeObserver = new ResizeObserver(() => {
      if (this.resizeDebounceTimer) clearTimeout(this.resizeDebounceTimer);
      this.resizeDebounceTimer = setTimeout(() => {
        this.handleResize();
      }, 120);
    });
    this.resizeObserver.observe(this.canvas.parentElement || this.canvas);
  }

  setupInteraction() {
    this.boundPointerMove = (event) => this.handleCanvasPointerMove(event);
    this.boundClick = (event) => this.handleCanvasClick(event);
    this.canvas.addEventListener('mousemove', this.boundPointerMove);
    this.canvas.addEventListener('click', this.boundClick);
    this.canvas.style.cursor = 'default';
  }

  initialResize() {
    const rect = this.canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;

    const logicalW = rect.width;
    const logicalH = rect.height;

    this.layers.resize(logicalW, logicalH, dpr);
  }

  handleResize() {
    const rect = this.canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;

    const logicalW = rect.width;
    const logicalH = rect.height;

    this.layers.resize(logicalW, logicalH, dpr);
    this.zoom = null;
    this.generation++;
    this.restart();
  }

  async start() {
    this.generation++;
    const gen = this.generation;
    this.checkpoints.clear();
    this.initializeInstructionalEvents();
    this.currentAnimationTimeline = [];

    this.nodes = this.compileNodes();

    await this.prepareAll(gen);
    if (gen !== this.generation) return;

    this.currentNodeIndex = this.resolveStartNodeIndex();
    this.startRenderLoop();
    await this.runNodes(gen);
  }

  resolveStartNodeIndex() {
    const requestedNodeId = this.pendingStartNodeId ? String(this.pendingStartNodeId) : null;
    this.pendingStartNodeId = null;
    if (!requestedNodeId) return 0;
    const index = this.slideOrder.indexOf(requestedNodeId);
    return index >= 0 ? index : 0;
  }

  compileNodes() {
    const compiled = [];

    for (const nodeSpec of this.lessonSpec.nodes) {
      const factory = this.registry.get(nodeSpec.kind);
      if (!factory) {
        continue;
      }

      const meta = {
        dwellBeforeSec:
          nodeSpec.meta?.dwellBeforeSec ?? this.lessonSpec.runtime.defaultDwellBeforeSec,
        dwellAfterSec: nodeSpec.meta?.dwellAfterSec ?? this.lessonSpec.runtime.defaultDwellAfterSec,
        transitionOut: nodeSpec.meta?.transitionOut ?? this.lessonSpec.runtime.defaultTransitionOut,
        estimatedDurationSec: nodeSpec.meta?.estimatedDurationSec,
      };

      const nodeDef = {
        id: nodeSpec.id,
        kind: nodeSpec.kind,
        instructionalEventId: this.eventByNodeId.get(nodeSpec.id)?.id || null,
        deps: nodeSpec.deps,
        meta,
        scene: {
          layout: nodeSpec.scene?.layout || null,
          animationPlan: normalizeAnimationPlan(nodeSpec.scene?.animationPlan),
          timeline: {
            waitForAnimationPlan: nodeSpec.scene?.timeline?.waitForAnimationPlan ?? true,
            requireSignalDone: nodeSpec.scene?.timeline?.requireSignalDone ?? false,
            maxWaitSec: Number.isFinite(nodeSpec.scene?.timeline?.maxWaitSec)
              ? Math.max(6, nodeSpec.scene.timeline.maxWaitSec)
              : 20,
          },
          physics: nodeSpec.scene?.physics || null,
          layers: nodeSpec.scene?.layers || null,
        },
        spec: nodeSpec.payload,
        prepare: (env) => factory.prepare(env, nodeSpec.payload),
        commit: (packet, ctxs, anim) => factory.commit(packet, ctxs, anim, nodeSpec.payload),
      };

      compiled.push(nodeDef);
    }

    if (compiled.length === 0) {
      this.topology = null;
      this.slideOrder = [];
      this.nodeRuntimeMeta = {};
      this.nodeById = new Map();
      return compiled;
    }

    const topology = createHeapTopologyState(compiled[0].id);
    const nodeRuntimeMeta = {};
    const nodeById = new Map(compiled.map((node) => [node.id, node]));

    compiled[0].heapIndex = 0;
    compiled[0].parentHeapIndex = null;
    compiled[0].childrenBySlot = [null, null, null, null];
    nodeRuntimeMeta[compiled[0].id] = {
      heapIndex: 0,
      parentHeapIndex: null,
      childrenBySlot: [null, null, null, null],
      slotRects: null,
      zoomSlotRects: null,
    };

    for (let i = 1; i < compiled.length; i++) {
      const node = compiled[i];
      const { heapIndex, parentHeapIndex } = appendNodeToHeap(topology, node.id);
      node.heapIndex = heapIndex;
      node.parentHeapIndex = parentHeapIndex;
      node.childrenBySlot = [null, null, null, null];

      nodeRuntimeMeta[node.id] = {
        heapIndex,
        parentHeapIndex,
        childrenBySlot: [null, null, null, null],
        slotRects: null,
        zoomSlotRects: null,
      };
    }

    for (let i = 1; i < compiled.length; i++) {
      const childNode = compiled[i];
      const parentNodeId = topology.heapIndexToNodeId[childNode.parentHeapIndex];
      const parentNode = nodeById.get(parentNodeId);
      const parentMeta = nodeRuntimeMeta[parentNodeId];

      if (!parentNode || !parentMeta) {
        throw new Error(`Topology invariant violated: missing parent for node ${childNode.id}`);
      }

      const emptySlot = parentMeta.childrenBySlot.findIndex((slotNodeId) => slotNodeId === null);
      if (emptySlot < 0) {
        throw new Error(
          `Topology invariant violated: parent ${parentNodeId} exceeded 4 children while attaching ${childNode.id}`
        );
      }

      parentMeta.childrenBySlot[emptySlot] = childNode.id;
      parentNode.childrenBySlot[emptySlot] = childNode.id;
    }

    this.topology = topology;
    this.slideOrder = buildSlideOrderFromHeap(topology.nodeIdToHeapIndex);
    this.nodeRuntimeMeta = nodeRuntimeMeta;
    this.nodeById = nodeById;

    return compiled;
  }

  async prepareAll(gen) {
    const concurrency = navigator.hardwareConcurrency || 4;
    const queue = [...this.nodes];
    const packets = new Map();

    const worker = async () => {
      while (queue.length > 0) {
        if (gen !== this.generation) return;
        const node = queue.shift();
        if (!node) continue;

        try {
          const env = this.createPrepareEnv(node.id);
          const packet = await node.prepare(env);
          const slotRects = computeFourSlotRegions(env.boardArea());
          const childCount = this.nodeRuntimeMeta[node.id]
            ? this.nodeRuntimeMeta[node.id].childrenBySlot.filter(Boolean).length
            : 0;
          const zoomSlotRects = computeSequentialLeftAlignedRegions(env.boardArea(), childCount);

          if (this.nodeRuntimeMeta[node.id]) {
            this.nodeRuntimeMeta[node.id].slotRects = slotRects;
            this.nodeRuntimeMeta[node.id].zoomSlotRects = zoomSlotRects;
          }
          if (packet && typeof packet === 'object') {
            packet.__topology = {
              ...(packet.__topology || {}),
              slotRects,
              zoomSlotRects,
            };
          }

          packets.set(node.id, packet);
        } catch (_error) {
          // Keep processing remaining nodes.
        }
      }
    };

    await Promise.all(
      Array(concurrency)
        .fill(0)
        .map(() => worker())
    );

    this.packets = packets;
  }

  createPrepareEnv(nodeId) {
    const canvas = this.canvas;
    const layers = this.layers;
    const theme = this.lessonSpec.theme;

    return {
      boardArea: () => layers.boardArea(),
      measureText: (text, size) => {
        const ctx = layers.getInkCtx();
        ctx.save();
        ctx.font = `${size}px ${theme.typography.sans}`;
        const metrics = ctx.measureText(text);
        ctx.restore();
        return metrics;
      },
      getStyle: (key) => {
        const h = canvas.height / (window.devicePixelRatio || 1);
        const scale = theme.typography.fluidScale;
        switch (key) {
          case 'titleSize':
            return fluidSize(scale.title.k, scale.title.min, h);
          case 'bodySize':
            return fluidSize(scale.body.k, scale.body.min, h);
          case 'codeSize':
            return fluidSize(scale.mono.k, scale.mono.min, h);
          case 'smallSize':
            return fluidSize(scale.small.k, scale.small.min, h);
          case 'lineHeight':
            return fluidSize(scale.lineGap.k, scale.lineGap.min, h);
          default:
            return 16;
        }
      },
      canvas: {
        width: canvas.width / (window.devicePixelRatio || 1),
        height: canvas.height / (window.devicePixelRatio || 1),
      },
      getNodeRuntimeMeta: () => (nodeId ? this.nodeRuntimeMeta[nodeId] || null : null),
    };
  }

  async runNodes(gen) {
    for (let i = this.currentNodeIndex; i < this.slideOrder.length; i++) {
      if (gen !== this.generation) return;

      const nodeId = this.slideOrder[i];
      const node = this.nodeById.get(nodeId);
      if (!node) continue;
      const instructionalEvent = this.getInstructionalEventForNode(node.id);
      this.currentNodeIndex = i;
      this.currentSlideId = node.id;
      this.slidePhase = 'mounting';
      this.animationStepIndex = -1;
      this.slideSignals.delete(node.id);
      if (instructionalEvent)
        this.markInstructionalEventLifecycle(instructionalEvent.id, 'entering');

      await this.dwellBefore(node.meta.dwellBeforeSec, gen);
      if (gen !== this.generation) return;

      if (i > 0) {
        this.slidePhase = 'exiting';
        await this.executeTransition(node.meta.transitionOut, gen);
        if (gen !== this.generation) return;
      } else {
        // Clear ink for the first node
        this.layers.clearInk();
      }

      this.commitNode(node);
      if (instructionalEvent) this.markInstructionalEventLifecycle(instructionalEvent.id, 'stable');

      this.slidePhase = 'animating';
      if (instructionalEvent)
        this.markInstructionalEventLifecycle(instructionalEvent.id, 'emphasized');
      await this.executeAnimationPlan(node, gen);
      if (gen !== this.generation) return;

      await this.waitForAnimations(gen);
      if (gen !== this.generation) return;
      await this.awaitSignalCompletion(node, gen);
      if (gen !== this.generation) return;

      this.slidePhase = 'completed';
      this.animationStepIndex = -1;
      if (instructionalEvent) {
        this.markInstructionalEventLifecycle(instructionalEvent.id, 'completed');
        if (instructionalEvent.checkpoint) {
          this.captureCheckpoint(instructionalEvent, node, i);
        }
      }
      await this.dwellAfter(node.meta.dwellAfterSec, gen);
      if (gen !== this.generation) return;
    }
  }

  async continuePlaybackAfterCurrentNode(gen) {
    const currentNodeId = this.slideOrder[this.currentNodeIndex];
    const currentNode = this.nodeById.get(currentNodeId);
    if (!currentNode) return;
    const currentEvent = this.getInstructionalEventForNode(currentNode.id);

    this.currentSlideId = currentNode.id;
    this.slidePhase = 'animating';
    if (currentEvent) this.markInstructionalEventLifecycle(currentEvent.id, 'emphasized');
    await this.executeAnimationPlan(currentNode, gen);
    if (gen !== this.generation) return;
    await this.waitForAnimations(gen);
    if (gen !== this.generation) return;
    await this.awaitSignalCompletion(currentNode, gen);
    if (gen !== this.generation) return;
    this.slidePhase = 'completed';
    this.animationStepIndex = -1;
    if (currentEvent) {
      this.markInstructionalEventLifecycle(currentEvent.id, 'completed');
      if (currentEvent.checkpoint) {
        this.captureCheckpoint(currentEvent, currentNode, this.currentNodeIndex);
      }
    }
    await this.dwellAfter(currentNode.meta.dwellAfterSec, gen);
    if (gen !== this.generation) return;

    for (let i = this.currentNodeIndex + 1; i < this.slideOrder.length; i++) {
      if (gen !== this.generation) return;

      const nodeId = this.slideOrder[i];
      const node = this.nodeById.get(nodeId);
      if (!node) continue;
      const instructionalEvent = this.getInstructionalEventForNode(node.id);
      this.currentNodeIndex = i;
      this.currentSlideId = node.id;
      this.slidePhase = 'mounting';
      this.animationStepIndex = -1;
      this.slideSignals.delete(node.id);
      if (instructionalEvent)
        this.markInstructionalEventLifecycle(instructionalEvent.id, 'entering');

      await this.dwellBefore(node.meta.dwellBeforeSec, gen);
      if (gen !== this.generation) return;

      this.slidePhase = 'exiting';
      await this.executeTransition(node.meta.transitionOut, gen);
      if (gen !== this.generation) return;

      this.commitNode(node);
      this.slidePhase = 'animating';
      if (instructionalEvent)
        this.markInstructionalEventLifecycle(instructionalEvent.id, 'emphasized');
      await this.executeAnimationPlan(node, gen);
      if (gen !== this.generation) return;
      await this.waitForAnimations(gen);
      if (gen !== this.generation) return;
      await this.awaitSignalCompletion(node, gen);
      if (gen !== this.generation) return;
      this.slidePhase = 'completed';
      this.animationStepIndex = -1;
      if (instructionalEvent) {
        this.markInstructionalEventLifecycle(instructionalEvent.id, 'completed');
        if (instructionalEvent.checkpoint) {
          this.captureCheckpoint(instructionalEvent, node, i);
        }
      }
      await this.dwellAfter(node.meta.dwellAfterSec, gen);
      if (gen !== this.generation) return;
    }
  }

  async executeAnimationPlan(node, gen) {
    const timeline = node.scene?.timeline || {};
    if (!timeline.waitForAnimationPlan) return;
    const plan = Array.isArray(node.scene?.animationPlan) ? node.scene.animationPlan : [];
    if (plan.length === 0) return;
    const schedule = planAnimationTimeline(plan);
    this.currentAnimationTimeline = schedule;
    const seek = this.pendingTimelineSeek;
    let startIndex = 0;
    if (seek && String(seek.nodeId || '') === String(node.id)) {
      const requestedStep = Number.isFinite(seek.step) ? Number(seek.step) : null;
      if (requestedStep !== null) {
        const matchedIndex = schedule.findIndex((step) => step.step >= requestedStep);
        startIndex = matchedIndex >= 0 ? matchedIndex : schedule.length;
      }
      this.pendingTimelineSeek = null;
    }

    let timelineCursor = startIndex > 0 ? schedule[startIndex - 1].endSec : 0;
    this.animationStepIndex = startIndex - 1;
    for (let i = startIndex; i < schedule.length; i++) {
      if (gen !== this.generation) return;
      const step = schedule[i];
      const waitGap = Math.max(0, step.startSec - timelineCursor);
      if (waitGap > 0) {
        await this.dwellBefore(waitGap, gen);
      }
      this.animationStepIndex = i;
      if (step.waitForSignal) {
        await this.waitForSlideSignal(node.id, timeline.maxWaitSec, gen);
      } else if (step.durationSec > 0) {
        await this.dwellBefore(step.durationSec, gen);
      }
      if (step.checkpoint) {
        this.captureTimelineCheckpoint(node, step);
      }
      timelineCursor = Math.max(timelineCursor, step.endSec);
      if (gen !== this.generation) return;
    }
  }

  async awaitSignalCompletion(node, gen) {
    if (!node.scene?.timeline?.requireSignalDone) return;
    await this.waitForSlideSignal(node.id, node.scene.timeline.maxWaitSec, gen);
  }

  async waitForSlideSignal(nodeId, maxWaitSec, gen) {
    const timeoutMs = Math.max(250, (maxWaitSec || 10) * 1000);
    const start = Date.now();
    return new Promise((resolve) => {
      const tick = () => {
        if (gen !== this.generation) {
          resolve();
          return;
        }
        if (this.slideSignals.get(nodeId)) {
          resolve();
          return;
        }
        if (Date.now() - start >= timeoutMs) {
          resolve();
          return;
        }
        setTimeout(tick, 30);
      };
      tick();
    });
  }

  signalAnimationDone(nodeId) {
    const id = nodeId || this.currentSlideId;
    if (!id) return;
    this.slideSignals.set(id, true);
  }

  commitNode(node) {
    const packet = this.packets.get(node.id);
    if (!packet) {
      return;
    }

    // Nodes may only draw on the ink layer.
    const ctxs = {
      ink: this.layers.getInkCtx(),
      interaction: {
        isPaused: () => this.paused,
        getInkCoverageInRect: (rect, options) => this.layers.getInkCoverageInRect(rect, options),
        hasInkInRect: (rect, options) => this.layers.hasInkInRect(rect, options),
        findLeastOccupiedRect: (candidates, rectSize, options) =>
          this.layers.findLeastOccupiedRect(candidates, rectSize, options),
      },
    };
    node.commit(packet, ctxs, this.anim);
  }

  async dwellBefore(duration, gen) {
    return new Promise((resolve) => {
      let t = 0;
      this.anim.add((dt) => {
        if (gen !== this.generation) {
          resolve();
          return false;
        }
        t += dt;
        if (t >= duration) {
          resolve();
          return false;
        }
        return true;
      });
    });
  }

  async dwellAfter(duration, gen) {
    return this.dwellBefore(duration, gen);
  }

  async executeTransition(type, gen) {
    if (gen !== this.generation) return;
    this.isTransitioning = true;
    try {
      switch (type) {
        case 'wipe':
          await this.transitions.wipeLeftToRight(0.56);
          break;
        case 'fade':
          await this.transitions.fade(0.4);
          break;
        case 'none':
          await this.transitions.none();
          break;
      }
    } finally {
      this.isTransitioning = false;
    }
  }

  async waitForAnimations(gen) {
    return new Promise((resolve) => {
      const check = () => {
        if (gen !== this.generation || !this.anim.active) {
          resolve();
        } else {
          setTimeout(check, 16);
        }
      };
      check();
    });
  }

  startRenderLoop() {
    if (this.rafId !== null) return;

    const tick = (timestamp) => {
      if (this.destroyed) return;

      const rawDt = this.lastTime === 0 ? 0 : Math.min((timestamp - this.lastTime) / 1000, 0.05);
      this.lastTime = timestamp;

      if (this.paused) {
        this.anim.tick(0);
        if (this.zoom) {
          this.tickZoom(rawDt);
        } else {
          if (this.manipulateMode) {
            this.renderManipulationOverlay();
          } else if (this.showRegionGuides) {
            this.renderRegionGuides();
          } else {
            this.clearManipulationOverlay();
          }
        }
        this.layers.composite();
        this.rafId = requestAnimationFrame(tick);
        return;
      }

      this.anim.tick(rawDt * this.speedFactor);
      if (this.showRegionGuides) {
        this.renderRegionGuides();
      } else {
        this.clearManipulationOverlay();
      }
      this.layers.composite();

      this.rafId = requestAnimationFrame(tick);
    };

    this.rafId = requestAnimationFrame(tick);
  }

  restart() {
    this.generation++;
    this.paused = false;
    this.manipulateMode = false;
    this.zoom = null;
    this.isTransitioning = false;
    this.currentSlideId = null;
    this.slidePhase = 'mounting';
    this.animationStepIndex = -1;
    this.currentAnimationTimeline = [];
    this.pendingPlaybackAfterManualNode = false;
    this.slideSignals.clear();
    this.resetInteractionState();
    this.anim.clear();
    this.layers.clearInk();
    this.layers.clearFx();
    this.layers.clearUi();
    this.currentNodeIndex = 0;
    this.start();
  }

  destroy() {
    this.destroyed = true;
    this.generation++;

    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.resizeDebounceTimer) {
      clearTimeout(this.resizeDebounceTimer);
      this.resizeDebounceTimer = null;
    }
    if (this.boundPointerMove) {
      this.canvas.removeEventListener('mousemove', this.boundPointerMove);
      this.boundPointerMove = null;
    }
    if (this.boundClick) {
      this.canvas.removeEventListener('click', this.boundClick);
      this.boundClick = null;
    }

    this.anim.clear();
    this.slideSignals.clear();
  }

  setSpeed(factor) {
    this.speedFactor = factor;
  }

  pause() {
    this.paused = true;
  }

  resume() {
    this.paused = false;
    this.manipulateMode = false;
    this.resetInteractionState();
    this.clearManipulationOverlay();
    if (this.pendingPlaybackAfterManualNode) {
      this.pendingPlaybackAfterManualNode = false;
      this.generation++;
      const gen = this.generation;
      this.continuePlaybackAfterCurrentNode(gen);
    }
  }

  togglePause() {
    this.paused = !this.paused;
    if (!this.paused) {
      this.manipulateMode = false;
      this.resetInteractionState();
      this.clearManipulationOverlay();
    }
    return this.paused;
  }

  isPaused() {
    return this.paused;
  }

  setRegionGuides(enabled) {
    this.showRegionGuides = Boolean(enabled);
    if (!this.showRegionGuides && !this.manipulateMode) {
      this.clearManipulationOverlay();
    }
    return this.showRegionGuides;
  }

  toggleRegionGuides() {
    return this.setRegionGuides(!this.showRegionGuides);
  }

  isRegionGuidesEnabled() {
    return this.showRegionGuides;
  }

  setManipulateMode(enabled) {
    if (!this.paused || this.zoom || this.isTransitioning) {
      this.manipulateMode = false;
      return false;
    }
    this.manipulateMode = Boolean(enabled);
    if (!this.manipulateMode) {
      this.resetInteractionState();
      this.clearManipulationOverlay();
    }
    return this.manipulateMode;
  }

  toggleManipulateMode() {
    return this.setManipulateMode(!this.manipulateMode);
  }

  isManipulateMode() {
    return this.manipulateMode;
  }

  resetInteractionState() {
    this.interaction = {
      hoverSlot: null,
      hoverChildId: null,
      selectedSlot: null,
      selectedChildId: null,
    };
    this.canvas.style.cursor = 'default';
  }

  getActiveNodeId() {
    return this.slideOrder[this.currentNodeIndex] || null;
  }

  getActiveSlotRects() {
    const activeNodeId = this.getActiveNodeId();
    if (!activeNodeId) return null;
    const meta = this.nodeRuntimeMeta[activeNodeId];
    if (!meta) return null;
    if (meta.parentHeapIndex === null) {
      return meta.slotRects || null;
    }
    return meta.zoomSlotRects || [];
  }

  resolveChildForSlot(slotIndex) {
    const activeNodeId = this.getActiveNodeId();
    if (!activeNodeId) return null;
    const meta = this.nodeRuntimeMeta[activeNodeId];
    if (!meta || !Array.isArray(meta.childrenBySlot)) return null;
    return meta.childrenBySlot[slotIndex] || null;
  }

  pointToCanvasCoords(event) {
    const rect = this.canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    const logicalW = this.canvas.width / dpr;
    const logicalH = this.canvas.height / dpr;
    const scaleX = rect.width > 0 ? logicalW / rect.width : 1;
    const scaleY = rect.height > 0 ? logicalH / rect.height : 1;

    return {
      x: (event.clientX - rect.left) * scaleX,
      y: (event.clientY - rect.top) * scaleY,
    };
  }

  hitTestSlot(x, y, slotRects) {
    if (!Array.isArray(slotRects)) return null;
    for (let i = 0; i < slotRects.length; i++) {
      const rect = slotRects[i];
      if (!rect) continue;
      const withinX = x >= rect.x && x <= rect.x + rect.w;
      const withinY = y >= rect.y && y <= rect.y + rect.h;
      if (withinX && withinY) return i;
    }
    return null;
  }

  handleCanvasPointerMove(event) {
    if (!this.paused || !this.manipulateMode) return;
    const slotRects = this.getActiveSlotRects();
    const point = this.pointToCanvasCoords(event);
    const slot = this.hitTestSlot(point.x, point.y, slotRects);
    this.interaction.hoverSlot = slot;
    this.interaction.hoverChildId = slot === null ? null : this.resolveChildForSlot(slot);
    this.canvas.style.cursor = slot === null ? 'default' : 'pointer';
  }

  handleCanvasClick(event) {
    if (!this.paused || !this.manipulateMode) return;
    const slotRects = this.getActiveSlotRects();
    const point = this.pointToCanvasCoords(event);
    const slot = this.hitTestSlot(point.x, point.y, slotRects);
    if (slot === null) return;
    this.interaction.selectedSlot = slot;
    this.interaction.selectedChildId = this.resolveChildForSlot(slot);
    if (this.interaction.selectedChildId) {
      this.startZoomToChild(slot, this.interaction.selectedChildId);
    }
  }

  startZoomToChild(slotIndex, childId) {
    if (!this.paused || this.zoom || this.isTransitioning) return false;
    if (!this.manipulateMode && this.interaction.selectedSlot === null) {
      this.resetInteractionState();
    }
    const activeSlotRects = this.getActiveSlotRects();
    const fromRect = Array.isArray(activeSlotRects) ? activeSlotRects[slotIndex] : null;
    if (!fromRect || !childId) return false;

    const snapshot = document.createElement('canvas');
    snapshot.width = this.canvas.width;
    snapshot.height = this.canvas.height;
    const snapCtx = snapshot.getContext('2d');
    if (!snapCtx) return false;
    snapCtx.drawImage(this.layers.inkCanvas, 0, 0);

    this.zoom = {
      childId,
      fromRect: { ...fromRect },
      snapshot,
      duration: 0.42,
      t: 0,
    };
    this.manipulateMode = false;
    this.clearManipulationOverlay();
    return true;
  }

  getZoomTargets() {
    const activeNodeId = this.getActiveNodeId();
    if (!activeNodeId) return [];
    const meta = this.nodeRuntimeMeta[activeNodeId];
    if (!meta?.childrenBySlot) return [];
    const slotNames = ['Explain', 'Visualize', 'Work Example', 'Checkpoint'];
    return meta.childrenBySlot
      .map((childId, slot) => ({
        slot,
        slotName: slotNames[slot] || `Slot ${slot}`,
        nodeId: childId,
      }))
      .filter((item) => Boolean(item.nodeId));
  }

  zoomToSlot(slotIndex) {
    if (!this.paused || this.zoom || this.isTransitioning) return false;
    const childId = this.resolveChildForSlot(slotIndex);
    if (!childId) return false;
    return this.startZoomToChild(slotIndex, childId);
  }

  zoomToNode(childNodeId) {
    if (!this.paused || this.zoom || this.isTransitioning) return false;
    const targets = this.getZoomTargets();
    const target = targets.find((item) => item.nodeId === childNodeId);
    if (!target) return false;
    return this.startZoomToChild(target.slot, target.nodeId);
  }

  tickZoom(dt) {
    if (!this.zoom) return;
    this.zoom.t += dt;
    const u = Math.max(0, Math.min(1, this.zoom.t / this.zoom.duration));
    const ease = 1 - (1 - u) ** 3;

    const { x, y, w, h } = this.zoom.fromRect;
    const boardW = this.layers.width;
    const boardH = this.layers.height;
    const sTarget = Math.max(boardW / Math.max(1, w), boardH / Math.max(1, h));
    const txTarget = -x * sTarget;
    const tyTarget = -y * sTarget;
    const s = 1 + (sTarget - 1) * ease;
    const tx = txTarget * ease;
    const ty = tyTarget * ease;

    const fx = this.layers.getFxCtx();
    this.layers.clearFx();
    fx.save();
    fx.setTransform(s, 0, 0, s, tx, ty);
    fx.drawImage(this.zoom.snapshot, 0, 0, this.layers.width, this.layers.height);
    fx.restore();

    if (u >= 1) {
      this.finishZoomToChild();
    }
  }

  finishZoomToChild() {
    if (!this.zoom) return;
    const childId = this.zoom.childId;
    this.zoom = null;
    this.layers.clearFx();

    const childIndex = this.slideOrder.indexOf(childId);
    if (childIndex < 0) return;

    this.generation++;
    this.anim.clear();

    this.currentNodeIndex = childIndex;
    this.layers.clearInk();
    this.layers.clearUi();
    const childNode = this.nodeById.get(childId);
    if (childNode) {
      this.commitNode(childNode);
      this.pendingPlaybackAfterManualNode = true;
    }
    this.resetInteractionState();
  }

  clearManipulationOverlay() {
    this.layers.clearUi();
  }

  renderManipulationOverlay() {
    if (!this.paused || !this.manipulateMode) {
      this.clearManipulationOverlay();
      return;
    }
    const slotRects = this.getActiveSlotRects();
    if (!Array.isArray(slotRects)) {
      this.clearManipulationOverlay();
      return;
    }

    const ui = this.layers.getUiCtx();
    this.layers.clearUi();
    ui.save();
    ui.lineWidth = 1.5;
    ui.setLineDash([6, 5]);

    for (let i = 0; i < slotRects.length; i++) {
      const rect = slotRects[i];
      const isHover = i === this.interaction.hoverSlot;
      const isSelected = i === this.interaction.selectedSlot;
      const hasChild = Boolean(this.resolveChildForSlot(i));

      ui.fillStyle = isSelected
        ? 'rgba(116, 196, 118, 0.18)'
        : isHover
          ? 'rgba(76, 175, 255, 0.14)'
          : 'rgba(255, 255, 255, 0.03)';
      ui.strokeStyle = isSelected
        ? 'rgba(116, 196, 118, 0.95)'
        : isHover
          ? 'rgba(76, 175, 255, 0.95)'
          : 'rgba(255, 255, 255, 0.28)';
      ui.fillRect(rect.x, rect.y, rect.w, rect.h);
      ui.strokeRect(rect.x, rect.y, rect.w, rect.h);

      const label = hasChild ? `Slot ${i} -> ${this.resolveChildForSlot(i)}` : `Slot ${i} (empty)`;
      ui.setLineDash([]);
      ui.fillStyle = 'rgba(255, 255, 255, 0.82)';
      ui.font = '12px ui-monospace, Menlo, Monaco, monospace';
      ui.fillText(label, rect.x + 8, rect.y + 18);
      ui.setLineDash([6, 5]);
    }

    ui.restore();
  }

  renderRegionGuides() {
    const slotRects = this.getActiveSlotRects();
    this.layers.clearUi();
    if (!Array.isArray(slotRects) || slotRects.length === 0) return;

    const ui = this.layers.getUiCtx();
    ui.save();
    ui.lineWidth = 1.2;
    ui.setLineDash([4, 4]);

    for (let i = 0; i < slotRects.length; i++) {
      const rect = slotRects[i];
      const childId = this.resolveChildForSlot(i);
      ui.strokeStyle = 'rgba(255, 255, 255, 0.35)';
      ui.fillStyle = 'rgba(255, 255, 255, 0.04)';
      ui.fillRect(rect.x, rect.y, rect.w, rect.h);
      ui.strokeRect(rect.x, rect.y, rect.w, rect.h);

      ui.setLineDash([]);
      ui.fillStyle = 'rgba(255, 255, 255, 0.78)';
      ui.font = '11px ui-monospace, Menlo, Monaco, monospace';
      ui.fillText(childId ? `slot ${i}: ${childId}` : `slot ${i}: empty`, rect.x + 8, rect.y + 16);
      ui.setLineDash([4, 4]);
    }

    ui.restore();
  }

  jumpTo(nodeId) {
    const index = this.slideOrder.indexOf(nodeId);
    if (index >= 0) {
      this.pendingStartNodeId = nodeId;
      this.restart();
    }
  }

  jumpToCheckpoint(eventId) {
    const checkpoint = this.checkpoints.get(eventId);
    if (!checkpoint?.nodeId) return false;
    if (Number.isFinite(checkpoint.timelineStep)) {
      this.pendingTimelineSeek = {
        nodeId: checkpoint.nodeId,
        step: Number(checkpoint.timelineStep),
      };
    } else {
      this.pendingTimelineSeek = null;
    }
    this.jumpTo(checkpoint.nodeId);
    return true;
  }

  getCheckpointIds() {
    return Array.from(this.checkpoints.keys());
  }

  getCheckpoints() {
    return Array.from(this.checkpoints.values()).map((item) => ({ ...item }));
  }

  getDebugSnapshot() {
    const activeNodeId = this.slideOrder[this.currentNodeIndex] || null;
    const renderProgramPackets = Array.from(this.packets.values()).filter(
      (packet) => packet && typeof packet === 'object' && packet.renderProgram
    ).length;
    return {
      topology: this.topology
        ? {
            nextInsertIndex: this.topology.nextInsertIndex,
            heapIndexToNodeId: { ...this.topology.heapIndexToNodeId },
            nodeIdToHeapIndex: { ...this.topology.nodeIdToHeapIndex },
          }
        : null,
      slideOrder: [...this.slideOrder],
      nodeRuntimeMeta: Object.fromEntries(
        Object.entries(this.nodeRuntimeMeta).map(([nodeId, meta]) => [
          nodeId,
          {
            heapIndex: meta.heapIndex,
            parentHeapIndex: meta.parentHeapIndex,
            childrenBySlot: [...meta.childrenBySlot],
            slotRects: Array.isArray(meta.slotRects)
              ? meta.slotRects.map((rect) => ({ ...rect }))
              : null,
            zoomSlotRects: Array.isArray(meta.zoomSlotRects)
              ? meta.zoomSlotRects.map((rect) => ({ ...rect }))
              : null,
          },
        ])
      ),
      activeNodeId,
      currentSlideId: this.currentSlideId,
      slidePhase: this.slidePhase,
      animationStepIndex: this.animationStepIndex,
      currentAnimationTimeline: Array.isArray(this.currentAnimationTimeline)
        ? this.currentAnimationTimeline.map((step) => ({ ...step }))
        : [],
      currentNodeIndex: this.currentNodeIndex,
      paused: this.paused,
      manipulateMode: this.manipulateMode,
      regionGuides: this.showRegionGuides,
      zooming: Boolean(this.zoom),
      transitioning: this.isTransitioning,
      speedFactor: this.speedFactor,
      pendingStartNodeId: this.pendingStartNodeId,
      pendingTimelineSeek: this.pendingTimelineSeek ? { ...this.pendingTimelineSeek } : null,
      instructionalEvents: this.instructionalEvents.map((evt) => ({
        id: evt.id,
        kind: evt.kind,
        nodeId: evt.payload?.nodeId || null,
        checkpoint: Boolean(evt.checkpoint),
        lifecycle: this.eventLifecycle.get(evt.id) || 'unborn',
      })),
      checkpointIds: this.getCheckpointIds(),
      renderProgramPackets,
      pedagogicalContractDiagnostics: [...this.contractDiagnostics],
      interaction: { ...this.interaction },
      zoomTargets: this.getZoomTargets(),
    };
  }

  initializeInstructionalEvents() {
    const events = Array.isArray(this.lessonSpec?.instructionalEvents)
      ? this.lessonSpec.instructionalEvents
      : [];
    const normalizedEvents = events.map((evt, idx) => ({
      id: String(evt?.id || `evt_${idx + 1}`),
      kind: String(evt?.kind || 'REVEAL_TERM'),
      payload: evt?.payload && typeof evt.payload === 'object' ? evt.payload : {},
      deps: Array.isArray(evt?.deps) ? evt.deps.map((d) => String(d)) : [],
      checkpoint: Boolean(evt?.checkpoint),
    }));
    this.instructionalEvents = planInstructionalTimeline(normalizedEvents);
    this.eventById = new Map(this.instructionalEvents.map((evt) => [evt.id, evt]));
    this.eventByNodeId = new Map();
    this.eventLifecycle = new Map();
    for (const evt of this.instructionalEvents) {
      const nodeId = evt?.payload?.nodeId ? String(evt.payload.nodeId) : null;
      if (nodeId && !this.eventByNodeId.has(nodeId)) {
        this.eventByNodeId.set(nodeId, evt);
      }
      this.eventLifecycle.set(evt.id, 'unborn');
    }
  }

  getInstructionalEventForNode(nodeId) {
    if (!nodeId) return null;
    return this.eventByNodeId.get(nodeId) || null;
  }

  markInstructionalEventLifecycle(eventId, lifecycle) {
    if (!eventId || !this.eventById.has(eventId)) return;
    this.eventLifecycle.set(eventId, lifecycle);
  }

  captureCheckpoint(event, node, nodeIndex) {
    if (!event || !event.id || !node?.id) return;
    this.checkpoints.set(event.id, {
      eventId: event.id,
      nodeId: node.id,
      nodeTitle: node.title || node.id,
      nodeIndex,
      slidePhase: this.slidePhase,
      animationStepIndex: this.animationStepIndex,
      capturedAt: Date.now(),
    });
  }

  captureTimelineCheckpoint(node, step) {
    if (!node?.id || !step?.step) return;
    const checkpointId = `timeline:${node.id}:step:${step.step}`;
    this.checkpoints.set(checkpointId, {
      eventId: checkpointId,
      nodeId: node.id,
      nodeTitle: node.title || node.id,
      nodeIndex: this.currentNodeIndex,
      slidePhase: this.slidePhase,
      animationStepIndex: this.animationStepIndex,
      timelineStep: step.step,
      capturedAt: Date.now(),
    });
  }
}
